import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CircleAlert as CircleAlertIcon,
  CircleCheck as CircleCheckIcon,
  Link as LinkIcon,
  LoaderCircle as LoaderCircleIcon,
} from 'lucide-react';
import { AndroidIcon, AppleIcon, ChromeIcon } from './BrandIcons';
import { useSSPWallet } from '@/hooks/useSSPWallet';
import { useAppDispatch } from '@/store/hooks';
import { updateUser } from '@/store/authSlice';
import { incrementOrgDataVersion } from '@/store/organizationSlice';
import { authApi } from '@/api/auth';
import { friendlyError } from '@/lib/errorMessage';

function FirefoxIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
      <path d="M20.452 5.943a8.86 8.86 0 0 0-1.79-2.322c.2.4.3.8.35 1.1a6.6 6.6 0 0 0-.98-1.342 6.1 6.1 0 0 0-4.1-2.063c-.2-.016-.4-.016-.6 0 2.1.9 3.1 2.9 3.3 4.1a4.7 4.7 0 0 0-2.8-1.3c-.1 0-.2-.01-.3-.01a3.9 3.9 0 0 0-2.1.57 3.4 3.4 0 0 0-1.5 2.73c0 .1 0 .2.01.3a2.8 2.8 0 0 1-.9-.5 3.4 3.4 0 0 1-1.1-1.7c-.55.68-.88 1.48-.97 2.34-.06.5-.04 1 .06 1.52a8.3 8.3 0 0 0 .4 1.46 7.9 7.9 0 0 0 3.7 4.1 8.1 8.1 0 0 0 4.4 1.1 8.2 8.2 0 0 0 4.8-1.7 8 8 0 0 0 2.8-4.3 8.5 8.5 0 0 0 .1-3.9 8.3 8.3 0 0 0-2.77-4.1z" />
    </svg>
  );
}

function BraveIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
      <path d="M20.3 6.4l.9-2.2-2.6-1.8h-4.1L12.6.8h-1.2L9.5 2.4H5.4L2.8 4.2l.9 2.2-.5 1.6c-.3.8-.5 1.7-.5 2.5 0 1 .3 2 .7 2.9.5 1 1.1 1.8 1.9 2.6l5.5 4.8.7.6.5.4.5-.4.7-.6 5.5-4.8c.8-.7 1.4-1.6 1.9-2.6.5-.9.7-1.9.7-2.9 0-.8-.2-1.7-.5-2.5l-.6-1.6zM12 18.3l-2.3-2 1.2-1.5h2.2l1.2 1.5-2.3 2zm4-4.3h-1l.6-1.4-2-1.2 1-2.2H12l-1.3.7H9.4l-1 2.2L6.5 13l.6 1.5h-.8l-1-3 1.5-3.6 1.5-.5 1.7-1.2h4l1.7 1.2 1.5.5 1.5 3.6-1 3z" />
    </svg>
  );
}

const CHROME_EXT_URL =
  'https://chromewebstore.google.com/detail/ssp-wallet/mgfbabcnedcejkfibpafadgkhmkifhbd';
const FIREFOX_EXT_URL = 'https://addons.mozilla.org/en-US/firefox/addon/ssp-wallet';
const GUIDE_URL = 'https://sspwallet.io/guide';
const IOS_URL = 'https://apps.apple.com/us/app/ssp-key/id6463717332';
const ANDROID_URL = 'https://play.google.com/store/apps/details?id=io.runonflux.sspkey';

export function SetupWalletBanner() {
  const { t } = useTranslation();
  const { isInstalled, isLoading: isWalletLoading, wkSign, error: walletError } = useSSPWallet();
  const dispatch = useAppDispatch();
  const [isLinking, setIsLinking] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkSuccess, setLinkSuccess] = useState(false);

  const handleLinkWallet = async () => {
    setLinkError(null);
    setIsLinking(true);

    try {
      const signResult = await wkSign('Link SSP Wallet to your account');
      if (!signResult) {
        // User cancelled or wallet error — walletError state handles display
        return;
      }

      const response = await authApi.linkWkIdentity({
        message: signResult.message,
        walletSignature: signResult.walletSignature,
        walletPubKey: signResult.walletPubKey,
        keySignature: signResult.keySignature ?? '',
        keyPubKey: signResult.keyPubKey ?? '',
        witnessScript: signResult.witnessScript,
        wkIdentity: signResult.wkIdentity,
        // EL-2: link-WK consumes a login challenge too, so it carries the token.
        ...(signResult.challengeToken ? { challengeToken: signResult.challengeToken } : {}),
      });

      dispatch(updateUser(response.user));
      dispatch(incrementOrgDataVersion());
      setLinkSuccess(true);
    } catch (err) {
      setLinkError(friendlyError(err, t, 'banners.setupWallet.linkFailed'));
    } finally {
      setIsLinking(false);
    }
  };

  const displayError = linkError || walletError;
  const isBusy = isLinking || isWalletLoading;

  if (linkSuccess) {
    return (
      <div className="setup-wallet-banner" style={{ borderColor: 'var(--color-success, #52c41a)' }}>
        <div className="setup-wallet-banner-header">
          <CircleCheckIcon
            className="setup-wallet-banner-icon"
            style={{ color: 'var(--color-success, #52c41a)' }}
          />
          <h3 className="setup-wallet-banner-title">{t('banners.setupWallet.linkedTitle')}</h3>
        </div>
        <p className="setup-wallet-banner-description">{t('banners.setupWallet.linkedDesc')}</p>
      </div>
    );
  }

  return (
    <div className="setup-wallet-banner">
      <div className="setup-wallet-banner-header">
        <CircleAlertIcon className="setup-wallet-banner-icon" />
        <h3 className="setup-wallet-banner-title">{t('banners.setupWallet.title')}</h3>
      </div>
      <p className="setup-wallet-banner-description">{t('banners.setupWallet.desc')}</p>
      <div className="setup-wallet-steps">
        <div className="setup-wallet-step">
          <span className="setup-wallet-step-number">1</span>
          <div className="setup-wallet-step-content">
            <span className="setup-wallet-step-text">{t('banners.setupWallet.step1')}</span>
            <div className="setup-wallet-step-links">
              <a href={CHROME_EXT_URL} target="_blank" rel="noopener noreferrer">
                <ChromeIcon /> Chrome
              </a>
              <a href={CHROME_EXT_URL} target="_blank" rel="noopener noreferrer">
                <BraveIcon /> Brave
              </a>
              <a href={FIREFOX_EXT_URL} target="_blank" rel="noopener noreferrer">
                <FirefoxIcon /> Firefox
              </a>
            </div>
          </div>
        </div>
        <div className="setup-wallet-step">
          <span className="setup-wallet-step-number">2</span>
          <div className="setup-wallet-step-content">
            <span className="setup-wallet-step-text">{t('banners.setupWallet.step2')}</span>
            <div className="setup-wallet-step-links">
              <a href={IOS_URL} target="_blank" rel="noopener noreferrer">
                <AppleIcon /> App Store
              </a>
              <a href={ANDROID_URL} target="_blank" rel="noopener noreferrer">
                <AndroidIcon /> Google Play
              </a>
            </div>
          </div>
        </div>
        <div className="setup-wallet-step">
          <span className="setup-wallet-step-number">3</span>
          <div className="setup-wallet-step-content">
            <span className="setup-wallet-step-text">{t('banners.setupWallet.step3')}</span>
          </div>
        </div>
        <div className="setup-wallet-step">
          <span className="setup-wallet-step-number">4</span>
          <div className="setup-wallet-step-content">
            <span className="setup-wallet-step-text">
              {isInstalled
                ? t('banners.setupWallet.step4Link')
                : t('banners.setupWallet.step4Install')}
            </span>
            <div style={{ marginTop: 8 }}>
              <button
                className="btn-primary"
                onClick={() => void handleLinkWallet()}
                disabled={!isInstalled || isBusy}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
              >
                {isBusy ? <LoaderCircleIcon className="lucide-spin" /> : <LinkIcon />}
                {isBusy ? t('banners.setupWallet.linking') : t('banners.setupWallet.linkWallet')}
              </button>
            </div>
            {displayError && (
              <div className="error-message" style={{ marginTop: 8, fontSize: 13 }}>
                {displayError}
              </div>
            )}
          </div>
        </div>
      </div>
      <a
        href={GUIDE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="setup-wallet-learn-more"
      >
        {t('banners.setupWallet.completeGuide')}
      </a>
    </div>
  );
}

export default SetupWalletBanner;
